/* eslint-disable react/no-unescaped-entities */
/* Questions tab: list, per-module bulk approve, gap analysis, LO-targeted
   draft panel, per-question editor, and the AI single-question generator. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, Edit3, Keyboard, Plus, RefreshCw, Save, Sparkles, Target, Trash2, X,
} from "lucide-react";
import { adminApi } from "../portal/shell";
import { SummaryChip } from "./coverage";
import { DraftCard } from "./DraftCard";
import { ReviewMode } from "./ReviewMode";

const COURSE_ID = "advanced-rpas-2026";

export function QuestionsTab({ module, courseModules = [], onChange }) {
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await adminApi.listQuestions(COURSE_ID, module.id);
      setQuestions(rows);
    } catch (e) { toast.error(e.message || "Failed to load questions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module.id]);

  const remove = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await adminApi.deleteQuestion(id);
      toast.success("Question deleted"); load(); onChange();
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  if (editing) {
    return (
      <QuestionEditor
        module={module} question={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); onChange(); }}
      />
    );
  }

  const placeholderCount = questions.filter((q) => q.is_placeholder).length;
  const aiDraftedCount = questions.filter((q) => q.is_ai_drafted).length;
  const authorPendingCount = questions.filter((q) => q.is_author_provided).length;
  const markedCount = questions.filter((q) => q.marked_for_reassign).length;
  const approvedCount = questions.filter((q) => !q.is_placeholder && !q.is_ai_drafted && !q.is_author_provided).length;

  const bulkApprove = async () => {
    if (!window.confirm(`Approve all ${authorPendingCount} author-pending question${authorPendingCount === 1 ? "" : "s"} in this module? They will immediately count toward the coverage target and become visible to students.`)) return;
    setBulkApproving(true);
    try {
      const r = await adminApi.approveAuthorPending(COURSE_ID, module.id);
      toast.success(`Approved ${r.approved} question${r.approved === 1 ? "" : "s"}`);
      await load();
      onChange();
    } catch (e) { toast.error(e.message || "Bulk approve failed"); }
    finally { setBulkApproving(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ color: "#a5b8b3", fontSize: 13, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span>{questions.length} total</span>
          <span style={{ color: "#3dc0af", fontSize: 11 }}>· {approvedCount} approved</span>
          {authorPendingCount > 0 && (
            <span style={{ color: "#6db8ff", fontSize: 11 }}>· {authorPendingCount} author pending</span>
          )}
          {aiDraftedCount > 0 && (
            <span style={{ color: "#9c6cf8", fontSize: 11 }}>· {aiDraftedCount} AI draft{aiDraftedCount === 1 ? "" : "s"}</span>
          )}
          {placeholderCount > 0 && (
            <span style={{ color: "#f0be3c", fontSize: 11 }}>· {placeholderCount} placeholder</span>
          )}
          {markedCount > 0 && (
            <span style={{ color: "#c9a8ff", fontSize: 11 }} data-testid="authoring-marked-count">
              · {markedCount} marked
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {authorPendingCount > 0 && (
            <button className="tbg-btn" onClick={bulkApprove} disabled={bulkApproving}
              data-testid="authoring-bulk-approve-author"
              style={{ background: "#6db8ff", color: "#04121f" }}
              title="Approve every author-pending question in this module in one click">
              <CheckCircle2 size={12} /> {bulkApproving ? "Approving…" : `Approve all ${authorPendingCount}`}
            </button>
          )}
          <button className="tbg-btn ghost" onClick={() => setReviewing(true)}
            data-testid="authoring-review-mode-open"
            disabled={questions.length === 0}
            style={{ borderColor: "rgba(156,108,248,0.5)", color: "#c9a8ff" }}
            title="Keyboard-driven review: walk every question in this module with hotkeys (K/M/E/A/D)">
            <Keyboard size={12} /> Review mode
          </button>
          <button className="tbg-btn ghost" onClick={() => setAnalyzing(true)}
            data-testid="authoring-analyze-gaps"
            style={{ borderColor: "rgba(156,108,248,0.5)", color: "#c9a8ff" }}
            title="Ask Claude which learning objectives still need question coverage">
            <Target size={12} /> Analyze gaps
          </button>
          <button className="tbg-btn" onClick={() => setEditing("new")} data-testid="authoring-question-new">
            <Plus size={13} /> New question
          </button>
        </div>
      </div>
      {loading ? (
        <div style={{ color: "#7b8b87" }}>Loading…</div>
      ) : questions.length === 0 ? (
        <div style={{ padding: 20, border: "1px dashed #2a403c", borderRadius: 4, color: "#7b8b87",
          fontSize: 13, textAlign: "center" }}>No questions yet.</div>
      ) : (
        <div>
          {questions.map((q, i) => (
            <div key={q.id} className="tbg-auth-modrow" data-testid={`authoring-question-row-${q.id}`}>
              <div className="code">Q{i + 1}</div>
              <div className="title">
                {q.is_placeholder && <span className="tbg-placeholder-tag" style={{ marginRight: 6 }}>PLACEHOLDER</span>}
                {q.is_ai_drafted && <span className="tbg-ai-tag" style={{ marginRight: 6 }} data-testid={`authoring-ai-tag-${q.id}`}>AI DRAFT</span>}
                {q.is_author_provided && <span className="tbg-author-tag" style={{ marginRight: 6 }} data-testid={`authoring-author-tag-${q.id}`}>AUTHOR PENDING</span>}
                {q.marked_for_reassign && (
                  <span style={{
                    display: "inline-block", marginRight: 6,
                    font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                    color: "#c9a8ff", padding: "2px 6px",
                    border: "1px solid rgba(156,108,248,0.5)", borderRadius: 3,
                    background: "rgba(156,108,248,0.08)",
                  }} data-testid={`authoring-marked-tag-${q.id}`}>MARKED</span>
                )}
                {q.prompt.slice(0, 100)}{q.prompt.length > 100 ? "…" : ""}
                <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
                  Scope: <span style={{ color: "#3dc0af" }}>{q.scope}</span> · Correct: option {q.correct_index + 1}
                  {q.is_ai_drafted && <span style={{ color: "#9c6cf8", marginLeft: 8 }}>· Awaiting review</span>}
                  {q.is_author_provided && <span style={{ color: "#6db8ff", marginLeft: 8 }}>· Awaiting spot-check</span>}
                  {q.learning_objective_ref && (
                    <span style={{ color: "#7b8b87", marginLeft: 8 }}>· LO: {q.learning_objective_ref}</span>
                  )}
                </div>
              </div>
              <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0 }}
                onClick={() => setEditing(q)} data-testid={`authoring-question-edit-${q.id}`}>
                <Edit3 size={11} /> {q.is_ai_drafted || q.is_author_provided ? "Review" : "Edit"}
              </button>
              <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0 }}
                onClick={() => remove(q.id)} data-testid={`authoring-question-delete-${q.id}`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {analyzing && (
        <CoverageGapPanel module={module} onClose={() => setAnalyzing(false)} />
      )}
      {reviewing && (
        <ReviewMode
          module={module}
          courseModules={courseModules}
          onClose={() => setReviewing(false)}
          onChange={() => { load(); onChange(); }}
        />
      )}
    </div>
  );
}

function CoverageGapPanel({ module, onClose }) {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [err, setErr] = useState("");
  const [drafting, setDrafting] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [reassigning, setReassigning] = useState(null); // question id being edited inline
  const [courseModules, setCourseModules] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        // Kick off both requests in parallel — analysis (LLM, ~5-15s) and
        // question list + course tree (fast) — so the reassign UI appears
        // before the analysis finishes.
        const [analysisRes, qs, course] = await Promise.all([
          adminApi.coverageGapAnalysis(COURSE_ID, module.id),
          adminApi.listQuestions(COURSE_ID, module.id),
          adminApi.courseAuthoring(COURSE_ID),
        ]);
        setAnalysis(analysisRes);
        setQuestions(qs);
        setCourseModules((course?.modules || []).filter((m) => !m.is_bonus));
      } catch (e) {
        setErr(e.message || "Analysis failed");
      } finally { setLoading(false); }
    })();
  }, [module.id]);

  const reload = async () => {
    try {
      const [qs, analysisRes] = await Promise.all([
        adminApi.listQuestions(COURSE_ID, module.id),
        adminApi.coverageGapAnalysis(COURSE_ID, module.id),
      ]);
      setQuestions(qs);
      setAnalysis(analysisRes);
    } catch (e) { toast.error(e.message || "Reload failed"); }
  };

  const statusColor = (s) => (s === "adequate" ? "#3dc0af" : s === "thin" ? "#f0be3c" : "#ef6a6a");
  const statusLabel = (s) => s === "adequate" ? "ADEQUATE" : s === "thin" ? "THIN" : "UNCOVERED";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="coverage-gap-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#9c6cf8" }}>
              <Target size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Coverage Gap Analysis · Module {module.code}
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              {module.title}
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4 }}>
              Compares approved (non-draft) questions against the module's learning
              objectives to show where you still need coverage.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="coverage-close">
            <X size={12} /> Close
          </button>
        </div>

        {loading && (
          <div style={{ padding: 30, textAlign: "center", color: "#c9a8ff", fontSize: 13 }}>
            <Sparkles size={14} className="spin" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Analyzing… (usually 5–10 seconds)
          </div>
        )}
        {err && <div className="tbg-lms-err" data-testid="coverage-err">{err}</div>}

        {analysis && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <SummaryChip color="#3dc0af" label="Adequate"
                count={analysis.los.filter((l) => l.coverage_status === "adequate").length} />
              <SummaryChip color="#f0be3c" label="Thin"
                count={analysis.los.filter((l) => l.coverage_status === "thin").length} />
              <SummaryChip color="#ef6a6a" label="Uncovered"
                count={analysis.los.filter((l) => l.coverage_status === "uncovered").length} />
              <SummaryChip color="#7b8b87" label="Approved Qs" count={analysis.approved_question_count} />
            </div>

            {analysis.recommendation && (
              <div style={{ padding: "12px 14px", background: "rgba(156,108,248,0.08)",
                border: "1px solid rgba(156,108,248,0.35)", color: "#c9a8ff", borderRadius: 4,
                fontSize: 13, lineHeight: 1.55, marginBottom: 14 }} data-testid="coverage-recommendation">
                <strong style={{ letterSpacing: "0.1em", fontSize: 10, textTransform: "uppercase" }}>Next action</strong>
                <div style={{ marginTop: 4 }}>{analysis.recommendation}</div>
              </div>
            )}

            {analysis.warnings && analysis.warnings.length > 0 && (
              <div style={{ padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4,
                fontSize: 12, marginBottom: 14 }} data-testid="coverage-warnings">
                <strong>Concerns:</strong>
                <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                  {analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {analysis.los.map((lo, i) => {
              const priorityRank = analysis.priorities.indexOf(i);
              return (
                <div key={i} data-testid={`coverage-lo-${i}`} style={{
                  padding: "12px 14px", marginBottom: 8,
                  background: "#0d1817", border: "1px solid #1c2a27", borderRadius: 4,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
                        color: "#7b8b87", textTransform: "uppercase", marginBottom: 4 }}>
                        LO {i + 1}
                        {priorityRank >= 0 && priorityRank < 3 && (
                          <span style={{ marginLeft: 8, color: "#9c6cf8" }}>· Priority #{priorityRank + 1}</span>
                        )}
                      </div>
                      <div style={{ color: "#e2ece9", fontSize: 13, lineHeight: 1.5 }}>{lo.objective}</div>
                      {lo.sample_question_prompts.length > 0 && (
                        <ul style={{ margin: "8px 0 0 18px", color: "#7b8b87", fontSize: 11, lineHeight: 1.55 }}>
                          {lo.sample_question_prompts.map((p, j) => <li key={j}>{p}</li>)}
                        </ul>
                      )}
                    </div>
                    <div style={{ textAlign: "right", minWidth: 110 }}>
                      <div style={{ font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                        color: statusColor(lo.coverage_status),
                        padding: "3px 8px", border: `1px solid ${statusColor(lo.coverage_status)}55`,
                        borderRadius: 3, display: "inline-block" }}
                        data-testid={`coverage-lo-status-${i}`}>
                        {statusLabel(lo.coverage_status)}
                      </div>
                      <div style={{ font: "600 11px 'JetBrains Mono', monospace",
                        color: statusColor(lo.coverage_status), marginTop: 4 }}>
                        {lo.question_count} question{lo.question_count === 1 ? "" : "s"}
                      </div>
                      {lo.coverage_status !== "adequate" && (
                        <button className="tbg-btn ghost" style={{
                          padding: "4px 8px", minWidth: 0, marginTop: 8,
                          borderColor: "rgba(156,108,248,0.5)", color: "#c9a8ff",
                        }}
                        onClick={() => setDrafting(lo.objective)}
                        data-testid={`coverage-lo-draft-${i}`}
                        title="Ask Claude to draft 5 diverse-angle questions targeting only this LO">
                          <Sparkles size={10} /> Draft 5
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="tbg-btn" onClick={onClose} data-testid="coverage-done">Done</button>
            </div>
          </>
        )}

        {/* Reassign / move — always visible once questions load, regardless of analysis state */}
        {questions.length > 0 && (
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #1c2a27" }}
            data-testid="coverage-reassign-section">
            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#7b8b87", textTransform: "uppercase", marginBottom: 10 }}>
              Reassign · move to different module or LO
            </div>
            <div style={{ color: "#7b8b87", fontSize: 12, marginBottom: 10, lineHeight: 1.55 }}>
              If the analyzer flags a question as not matching any LO, or you spot it in the wrong
              module, use <b>Move</b> to fix it in place. Any change invalidates this module's
              cached analysis so re-running gives you a fresh, updated verdict.
            </div>
            {questions.map((q, i) => (
              <ReassignRow
                key={q.id}
                question={q}
                index={i}
                courseModules={courseModules}
                isEditing={reassigning === q.id}
                onEdit={() => setReassigning(q.id)}
                onCancel={() => setReassigning(null)}
                onSaved={() => { setReassigning(null); reload(); }}
              />
            ))}
          </div>
        )}

        {drafting && (
          <LoDrafterPanel module={module} targetLo={drafting} onClose={() => setDrafting(null)} />
        )}
      </div>
    </div>
  );
}

function ReassignRow({ question, index, courseModules, isEditing, onEdit, onCancel, onSaved }) {
  const [targetModuleId, setTargetModuleId] = useState(question.module_id);
  const [loRef, setLoRef] = useState(question.learning_objective_ref || "");
  const [saving, setSaving] = useState(false);

  const dirty = targetModuleId !== question.module_id
    || (loRef || "").trim() !== (question.learning_objective_ref || "").trim();

  const save = async () => {
    if (!dirty) { onCancel(); return; }
    setSaving(true);
    try {
      await adminApi.reassignQuestion(question.id, {
        module_id: targetModuleId !== question.module_id ? targetModuleId : undefined,
        learning_objective_ref: (loRef || "").trim() === (question.learning_objective_ref || "").trim()
          ? undefined : loRef,
      });
      toast.success("Question reassigned");
      onSaved();
    } catch (e) { toast.error(e.message || "Reassign failed"); }
    finally { setSaving(false); }
  };

  const stateLabel = question.is_placeholder ? "PLACEHOLDER"
    : question.is_ai_drafted ? "AI DRAFT"
    : question.is_author_provided ? "AUTHOR PENDING" : "APPROVED";
  const stateColor = question.is_placeholder ? "#f0be3c"
    : question.is_ai_drafted ? "#9c6cf8"
    : question.is_author_provided ? "#6db8ff" : "#3dc0af";

  return (
    <div style={{ padding: "10px 12px", marginBottom: 6, background: "#0d1817",
      border: `1px solid ${isEditing ? "rgba(156,108,248,0.4)" : "#1c2a27"}`, borderRadius: 4 }}
      data-testid={`reassign-row-${question.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
            <span style={{ font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87" }}>Q{index + 1}</span>
            <span style={{ font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: stateColor, padding: "1px 6px",
              border: `1px solid ${stateColor}55`, borderRadius: 2 }}>{stateLabel}</span>
            {question.learning_objective_ref && (
              <span style={{ color: "#7b8b87", fontSize: 11 }}>
                LO: <em style={{ color: "#a5b8b3" }}>{question.learning_objective_ref}</em>
              </span>
            )}
            {!question.learning_objective_ref && (
              <span style={{ color: "#7b8b87", fontSize: 11, fontStyle: "italic" }}>no LO tag</span>
            )}
          </div>
          <div style={{ color: "#e2ece9", fontSize: 12, lineHeight: 1.4 }}>
            {question.prompt.slice(0, 130)}{question.prompt.length > 130 ? "…" : ""}
          </div>
        </div>
        {!isEditing && (
          <button className="tbg-btn ghost" style={{
            padding: "4px 10px", minWidth: 0,
            borderColor: "rgba(156,108,248,0.5)", color: "#c9a8ff",
          }} onClick={onEdit} data-testid={`reassign-btn-${question.id}`}>
            Move
          </button>
        )}
      </div>
      {isEditing && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #1c2a27",
          display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
              Target module
            </label>
            <select value={targetModuleId} onChange={(e) => setTargetModuleId(e.target.value)}
              data-testid={`reassign-module-select-${question.id}`}
              style={{ width: "100%", background: "#050807", border: "1px solid #1c2a27",
                borderRadius: 4, padding: "6px 10px", color: "#e2ece9",
                font: "500 12px 'JetBrains Mono', monospace" }}>
              {courseModules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.title}{m.id === question.module_id ? " (current)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
              Learning objective reference (blank = clear)
            </label>
            <textarea value={loRef} onChange={(e) => setLoRef(e.target.value)}
              data-testid={`reassign-lo-textarea-${question.id}`}
              placeholder="e.g. Explain the effect of bank angle on turn radius (CARs 901.62)"
              style={{ width: "100%", minHeight: 50, background: "#050807",
                border: "1px solid #1c2a27", borderRadius: 4, padding: "6px 10px",
                color: "#e2ece9", font: "500 11px 'JetBrains Mono', monospace" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <button className="tbg-btn ghost" onClick={onCancel} style={{ padding: "5px 10px" }}>Cancel</button>
            <button className="tbg-btn" onClick={save} disabled={saving || !dirty}
              data-testid={`reassign-save-${question.id}`}
              style={{ background: "#9c6cf8", padding: "5px 12px" }}>
              {saving ? "Saving…" : "Save reassignment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LoDrafterPanel({ module, targetLo, onClose }) {
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.batchDraftForLo(COURSE_ID, module.id, targetLo);
        setBatch(r);
      } catch (e) {
        setErr(e.message || "Batch draft failed");
      } finally { setLoading(false); }
    })();
  }, [module.id, targetLo]);

  const saveOne = async (i) => {
    if (saved[i] || saving[i]) return;
    setSaving((s) => ({ ...s, [i]: true }));
    const draft = batch.drafts[i];
    try {
      const created = await adminApi.createQuestion(COURSE_ID, module.id, {
        prompt: draft.prompt,
        options: draft.options,
        correct_index: draft.correct_index,
        rationale: draft.rationale,
        scope: "both",
        is_placeholder: false,
        is_ai_drafted: true,
      });
      setSaved((s) => ({ ...s, [i]: created.id }));
      toast.success(`Draft ${i + 1} staged — awaiting review`);
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving((s) => ({ ...s, [i]: false })); }
  };

  const saveAllUnsaved = async () => {
    if (bulkSaving) return;
    setBulkSaving(true);
    try {
      for (let i = 0; i < batch.drafts.length; i++) {
        if (saved[i]) continue;
        await saveOne(i);
      }
    } finally { setBulkSaving(false); }
  };

  const total = batch?.drafts?.length || 0;
  const savedCount = Object.values(saved).filter(Boolean).length;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 110,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="lo-drafter-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div className="tbg-section-sub" style={{ color: "#9c6cf8" }}>
              <Sparkles size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Draft 5 · Targeting one LO
            </div>
            <div style={{ marginTop: 6, padding: "8px 12px", background: "rgba(156,108,248,0.08)",
              border: "1px solid rgba(156,108,248,0.3)", borderRadius: 4,
              color: "#e2ece9", fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ letterSpacing: "0.1em", fontSize: 10, textTransform: "uppercase",
                color: "#c9a8ff" }}>Target LO</strong>
              <div style={{ marginTop: 4 }}>{targetLo}</div>
            </div>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 8 }}>
              All 5 questions target this LO; angles vary (recall / application /
              distinguishing / boundary / consequence).
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="lo-drafter-close">
            <X size={12} /> Close
          </button>
        </div>

        {loading && (
          <div style={{ padding: 30, textAlign: "center", color: "#c9a8ff", fontSize: 13 }}>
            <Sparkles size={14} className="spin" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Claude is drafting 5 angle-diverse questions for this LO…
          </div>
        )}
        {err && <div className="tbg-lms-err" data-testid="lo-drafter-err">{err}</div>}

        {batch && (
          <>
            {batch.warnings && batch.warnings.length > 0 && (
              <div style={{ marginBottom: 14, padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4, fontSize: 12 }}
                data-testid="lo-drafter-warnings">
                <strong>Concerns:</strong>
                <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                  {batch.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
              <span>{savedCount} / {total} staged as AI drafts</span>
              {savedCount < total && (
                <button className="tbg-btn" style={{ background: "#9c6cf8", padding: "6px 14px" }}
                  onClick={saveAllUnsaved} disabled={bulkSaving}
                  data-testid="lo-drafter-save-all">
                  {bulkSaving ? "Staging…" : `Stage all remaining (${total - savedCount})`}
                </button>
              )}
            </div>
            {batch.drafts.map((d, i) => (
              <DraftCard
                key={i} draft={d} index={i}
                saved={!!saved[i]} saving={!!saving[i]}
                onSave={() => saveOne(i)}
              />
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 8 }}>
              <button className="tbg-btn" onClick={onClose} data-testid="lo-drafter-done">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function QuestionEditor({ module, question, onClose, onSaved }) {
  const [prompt, setPrompt] = useState(question?.prompt || "");
  const [options, setOptions] = useState(question?.options || ["", "", "", ""]);
  const [correct, setCorrect] = useState(question?.correct_index ?? 0);
  const [rationale, setRationale] = useState(question?.rationale || "");
  const [scope, setScope] = useState(question?.scope || "both");
  const [isPlaceholder, setIsPlaceholder] = useState(!!question?.is_placeholder);
  const [isAiDrafted, setIsAiDrafted] = useState(!!question?.is_ai_drafted);
  const [isAuthorProvided, setIsAuthorProvided] = useState(!!question?.is_author_provided);
  const [saving, setSaving] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  const save = async (opts = {}) => {
    // Explicit-intent flags avoid stale-closure bugs where a just-fired
    // setter (isAiDrafted → false) hadn't landed yet when the Save-as-approved
    // callback ran.
    const asDraft = !!opts.asDraft;
    const approve = !!opts.approve;
    const keepAuthorPending = !!opts.keepAuthorPending;
    if (!prompt.trim()) return toast.error("Prompt required");
    if (options.some((o) => !o.trim())) return toast.error("All options must be filled");
    if (correct >= options.length) return toast.error("Correct option out of range");

    let finalAiDrafted;
    if (approve) finalAiDrafted = false;
    else if (asDraft) finalAiDrafted = true;
    else finalAiDrafted = isAiDrafted;

    let finalAuthorProvided;
    if (approve) finalAuthorProvided = false;
    else if (keepAuthorPending) finalAuthorProvided = true;
    else finalAuthorProvided = isAuthorProvided;

    setSaving(true);
    try {
      const body = {
        prompt: prompt.trim(),
        options: options.map((o) => o.trim()),
        correct_index: parseInt(correct, 10),
        rationale: rationale.trim(),
        scope,
        is_placeholder: isPlaceholder,
        is_ai_drafted: finalAiDrafted,
        is_author_provided: finalAuthorProvided,
        learning_objective_ref: question?.learning_objective_ref || null,
      };
      if (question) {
        await adminApi.updateQuestion(question.id, body);
        const wasDraft = !!question.is_ai_drafted;
        const wasAuthorPending = !!question.is_author_provided;
        if ((wasDraft || wasAuthorPending) && !finalAiDrafted && !finalAuthorProvided) {
          toast.success("Question approved");
        } else if (finalAiDrafted) {
          toast.success("Saved as AI draft");
        } else if (finalAuthorProvided) {
          toast.success("Kept as author-pending");
        } else {
          toast.success("Question saved");
        }
      } else {
        await adminApi.createQuestion(COURSE_ID, module.id, body);
        toast.success(asDraft ? "Saved as AI draft — needs review" : "Question created");
      }
      setIsAiDrafted(finalAiDrafted);
      setIsAuthorProvided(finalAuthorProvided);
      onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const applyDraft = (draft) => {
    setPrompt(draft.prompt);
    setOptions(draft.options);
    setCorrect(draft.correct_index);
    setRationale(draft.rationale);
    setIsAiDrafted(true);
    setShowAiPanel(false);
    toast.info("AI draft loaded — review, edit, then save.");
  };

  const setOpt = (i, v) => setOptions((arr) => arr.map((o, idx) => idx === i ? v : o));
  const addOpt = () => options.length < 6 && setOptions([...options, ""]);
  const removeOpt = (i) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, idx) => idx !== i));
    if (correct >= options.length - 1) setCorrect(0);
  };

  const isReviewingDraft = !!question && !!question.is_ai_drafted;
  const isReviewingAuthor = !!question && !!question.is_author_provided;

  return (
    <div className="tbg-auth-editor" data-testid="authoring-question-editor">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <strong style={{
          color: isReviewingDraft ? "#9c6cf8" : isReviewingAuthor ? "#6db8ff" : "#3dc0af",
          letterSpacing: "0.14em", fontSize: 11, textTransform: "uppercase" }}>
          {isReviewingDraft ? "Review AI Draft"
            : isReviewingAuthor ? "Review author-provided question"
            : question ? "Edit question" : "New question"}
        </strong>
        <div style={{ display: "flex", gap: 6 }}>
          {!question && (
            <button className="tbg-btn ghost" style={{ padding: "5px 10px" }}
              onClick={() => setShowAiPanel(true)} data-testid="authoring-open-ai-generator">
              <Sparkles size={12} /> Draft with AI
            </button>
          )}
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}>
            <X size={12} /> Close
          </button>
        </div>
      </div>

      {isReviewingDraft && (
        <div style={{ padding: "10px 12px", background: "rgba(156,108,248,0.08)",
          border: "1px solid rgba(156,108,248,0.4)", borderRadius: 4, color: "#c9a8ff",
          fontSize: 12, lineHeight: 1.5 }} data-testid="authoring-review-notice">
          <b>AI-drafted, not yet reviewed.</b> Read the prompt, options, and rationale carefully.
          Edit anything that isn't accurate, then either <b>Save as approved</b> to release it to
          the exam pool, or <b>Save as AI draft</b> to keep it out of student view.
        </div>
      )}
      {isReviewingAuthor && (
        <div style={{ padding: "10px 12px", background: "rgba(109,184,255,0.08)",
          border: "1px solid rgba(109,184,255,0.4)", borderRadius: 4, color: "#a9d2f4",
          fontSize: 12, lineHeight: 1.5 }} data-testid="authoring-author-review-notice">
          <b>Human-authored, imported, awaiting spot-check.</b>
          {question?.learning_objective_ref && (
            <span> Targets LO: <em style={{ color: "#e2ece9" }}>{question.learning_objective_ref}</em>.</span>
          )}
          {" "}Edit if needed, then either <b>Save as approved</b> to promote it into the exam
          pool, or <b>Keep pending</b> to leave it out until a full review pass.
        </div>
      )}

      <label>Prompt</label>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} style={{ minHeight: 80 }}
        data-testid="authoring-question-prompt" placeholder="Which airspace class requires ATC authorization…" />

      <label>Options (select the correct one)</label>
      {options.map((o, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <input type="radio" name="correct" checked={correct === i} onChange={() => setCorrect(i)}
            data-testid={`authoring-question-correct-${i}`} style={{ accentColor: "#3dc0af" }} />
          <input value={o} onChange={(e) => setOpt(i, e.target.value)} style={{ flex: 1 }}
            data-testid={`authoring-question-option-${i}`} placeholder={`Option ${i + 1}`} />
          <button className="tbg-btn ghost" style={{ padding: "5px 8px", minWidth: 0 }}
            onClick={() => removeOpt(i)} disabled={options.length <= 2}>
            <X size={11} />
          </button>
        </div>
      ))}
      {options.length < 6 && (
        <button className="tbg-btn ghost" style={{ padding: "5px 10px", alignSelf: "flex-start" }} onClick={addOpt}>
          <Plus size={11} /> Add option
        </button>
      )}

      <label>Rationale (shown to student on review)</label>
      <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} style={{ minHeight: 60 }}
        placeholder="Cite TP 15263 §… or CARs …" />

      <div className="row">
        <div style={{ flex: 1 }}>
          <label>Scope</label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="authoring-question-scope">
            <option value="both">Both (module quiz + exam pool)</option>
            <option value="module">Module quiz only</option>
            <option value="exam">Exam pool only</option>
          </select>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, justifyContent: "flex-end" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
            <input type="checkbox" checked={isPlaceholder} onChange={(e) => setIsPlaceholder(e.target.checked)}
              style={{ accentColor: "#f0be3c" }} data-testid="authoring-question-placeholder" />
            Placeholder
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
            <input type="checkbox" checked={isAiDrafted} onChange={(e) => setIsAiDrafted(e.target.checked)}
              style={{ accentColor: "#9c6cf8" }} data-testid="authoring-question-ai-drafted" />
            AI draft (unreviewed)
          </label>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button className="tbg-btn ghost" onClick={onClose}>Cancel</button>
        {isReviewingDraft ? (
          <>
            <button className="tbg-btn ghost" onClick={() => save({ asDraft: true })} disabled={saving}
              data-testid="authoring-question-save-draft"
              title="Keep flagged as AI draft (won't reach students)">
              <Save size={13} /> Save as AI draft
            </button>
            <button className="tbg-btn" onClick={() => save({ approve: true })}
              disabled={saving} data-testid="authoring-question-approve"
              style={{ background: "#3dc0af" }}>
              <CheckCircle2 size={13} /> Save as approved
            </button>
          </>
        ) : isReviewingAuthor ? (
          <>
            <button className="tbg-btn ghost" onClick={() => save({ keepAuthorPending: true })} disabled={saving}
              data-testid="authoring-question-keep-author-pending"
              title="Keep flagged as author-pending (won't reach students)">
              <Save size={13} /> Keep pending
            </button>
            <button className="tbg-btn" onClick={() => save({ approve: true })}
              disabled={saving} data-testid="authoring-question-approve"
              style={{ background: "#3dc0af" }}>
              <CheckCircle2 size={13} /> Save as approved
            </button>
          </>
        ) : (
          <button className="tbg-btn" onClick={() => save()} disabled={saving}
            data-testid="authoring-question-save">
            <Save size={13} /> {saving ? "Saving…" : "Save question"}
          </button>
        )}
      </div>

      {showAiPanel && (
        <AiGeneratorPanel
          module={module}
          onClose={() => setShowAiPanel(false)}
          onDraft={applyDraft}
        />
      )}
    </div>
  );
}

function AiGeneratorPanel({ module, onClose, onDraft }) {
  const [sourceText, setSourceText] = useState("");
  const [extraHint, setExtraHint] = useState("");
  const [difficulty, setDifficulty] = useState("advanced");
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [preview, setPreview] = useState(null);

  const generate = async () => {
    if (sourceText.trim().length < 30) {
      return toast.error("Paste at least a paragraph of reference text.");
    }
    setBusy(true); setWarnings([]); setPreview(null);
    try {
      const d = await adminApi.draftQuestion(COURSE_ID, module.id, {
        source_text: sourceText,
        extra_hint: extraHint || undefined,
        difficulty,
      });
      setPreview(d);
      setWarnings(d.warnings || []);
    } catch (e) {
      toast.error(e.message || "Draft failed");
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100,
      display: "grid", placeItems: "center", padding: 20,
    }} data-testid="ai-generator-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#9c6cf8" }}>
              <Sparkles size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              AI Question Assistant
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Draft a question from source text
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4 }}>
              Powered by Claude Sonnet 4.6 · Every draft requires human review before it can reach students.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}>
            <X size={12} /> Close
          </button>
        </div>

        {!preview && (
          <>
            <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase" }}>Source text (paste your licensed reference)</label>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder="Paste a chunk of TP 15263E, CARs Part IX, AC 903-001, or your own vetted study notes. The AI will draft ONE question grounded in this text."
              style={{
                width: "100%", minHeight: 180, marginTop: 6,
                background: "#050807", border: "1px solid #1c2a27", borderRadius: 4,
                padding: "10px 12px", color: "#e2ece9",
                font: "500 12px 'JetBrains Mono', monospace",
              }}
              data-testid="ai-source-text"
            />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
                  color: "#7b8b87", textTransform: "uppercase" }}>Topic hint (optional)</label>
                <input
                  value={extraHint}
                  onChange={(e) => setExtraHint(e.target.value)}
                  placeholder="e.g. 100 ft bystander rule"
                  style={{
                    width: "100%", marginTop: 6,
                    background: "#050807", border: "1px solid #1c2a27", borderRadius: 4,
                    padding: "8px 10px", color: "#e2ece9",
                    font: "500 12px 'JetBrains Mono', monospace",
                  }}
                  data-testid="ai-extra-hint"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
                  color: "#7b8b87", textTransform: "uppercase" }}>Difficulty</label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  style={{
                    width: "100%", marginTop: 6,
                    background: "#050807", border: "1px solid #1c2a27", borderRadius: 4,
                    padding: "8px 10px", color: "#e2ece9",
                    font: "500 12px 'JetBrains Mono', monospace",
                  }}
                >
                  <option value="basic">Basic recall</option>
                  <option value="advanced">Advanced (recommended)</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <button className="tbg-btn ghost" onClick={onClose}>Cancel</button>
              <button className="tbg-btn" onClick={generate} disabled={busy}
                data-testid="ai-generate-btn"
                style={{ background: "#9c6cf8" }}>
                <Sparkles size={13} /> {busy ? "Drafting…" : "Generate draft"}
              </button>
            </div>
          </>
        )}

        {preview && (
          <div data-testid="ai-preview">
            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
              color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
              Draft preview · model {preview.model}
            </div>
            <div className="tbg-qcard">
              <p className="tbg-qtext">{preview.prompt}</p>
              {preview.options.map((opt, idx) => (
                <div key={idx} className={`tbg-qopt${idx === preview.correct_index ? " correct" : ""}`}>
                  <strong style={{ marginRight: 6 }}>{String.fromCharCode(65 + idx)}.</strong>{opt}
                </div>
              ))}
              {preview.rationale && (
                <div className="tbg-rationale">
                  <strong style={{ color: "#3dc0af", letterSpacing: "0.06em", fontSize: 11, textTransform: "uppercase" }}>Why: </strong>
                  {preview.rationale}
                </div>
              )}
            </div>
            {warnings.length > 0 && (
              <div style={{ marginTop: 10, padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4, fontSize: 12 }}
                data-testid="ai-warnings">
                <strong>AI raised concerns:</strong>
                <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(156,108,248,0.08)",
              border: "1px solid rgba(156,108,248,0.35)", color: "#c9a8ff", borderRadius: 4, fontSize: 12,
              lineHeight: 1.5 }}>
              This will be loaded into the editor <b>flagged as an AI draft</b>. It won't reach
              students until you review, edit as needed, and click <b>Save as approved</b>.
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 14 }}>
              <button className="tbg-btn ghost" onClick={() => setPreview(null)} data-testid="ai-regenerate">
                <RefreshCw size={13} /> Try again
              </button>
              <button className="tbg-btn" onClick={() => onDraft(preview)} data-testid="ai-load-into-editor"
                style={{ background: "#9c6cf8" }}>
                <Sparkles size={13} /> Load into editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
